import {useMemo} from "react";
import {css, Theme, useTheme} from "@emotion/react";

import {Button} from "@pg-design/button-module";
import {CartTimerIcon, HotIcon, MegaphoneIcon, PercentIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";

import {InquiryButtonTypeName} from "../../../constants/inquiry_button_type";
import {ApplicationModalButton, IApplicationModalButtonRenderProps} from "../../application/components/ApplicationModalButton";
import {ApplicationModalStep} from "../../application/types/ApplicationModalStep";
import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {IGTMEventLeadForm} from "../../tracking/google_tag_manager/gtm_event_form_open";
import {getPromotionTypeName, PromotionType} from "../helpers/PromotionType";
import {useAmplitudePromotionViewportTracking} from "../hooks/use_amplitude_promotion_viewport_tracking";
import {getAmplitudeOfferPagePromotionTrackingContext} from "../tracking/get_amplitude_offer_page_promotion_tracking_context";
import {getAmplitudePromotionLeadFormTrackingContext} from "../tracking/get_amplitude_promotion_lead_form_event_tracking";
import {trackAmplitudeOfferSelectPromotion} from "../tracking/track_amplitude_offer_promotion_events";

interface IProps {
    promotion: {
        description: string | null;
        id: number;
        name: string;
        type: PromotionType;
    };
    vendorId: number;
    isAutoresponderEnabled: boolean;
    offerId?: number;
    propertyId?: number;
    isSaleOverLimit?: boolean;
}

export const Promotion = (props: IProps) => {
    const {promotion, vendorId, offerId, propertyId} = props;

    const promotionStyle = usePromotionStyle(promotion.type);
    const applicationSource = propertyId ? ApplicationSource.PromotionProperty : ApplicationSource.Promotion;
    const gtmData: IGTMEventLeadForm = {
        clickText: InquiryButtonTypeName.PROMOTIONS,
        source: GTMEventApplicationSourceSection.PROMOTION,
        vendorName: vendorId.toString(),
        offerName: offerId ? offerId.toString() : ""
    };

    const trackingContext = useMemo(() => {
        if (!offerId) {
            return null;
        }

        return getAmplitudeOfferPagePromotionTrackingContext({
            promotion,
            offerId,
            propertyId,
            creativeSlot: "promotion_box"
        });
    }, [offerId, propertyId, promotion]);

    const {elementId} = useAmplitudePromotionViewportTracking({
        trackingContext,
        elementId: promotion.id.toString(),
        disabled: !offerId
    });

    const amplitudeLeadFormTrackingContext = useMemo(() => {
        if (!trackingContext) {
            return null;
        }

        return getAmplitudePromotionLeadFormTrackingContext({
            promotionTrackingContext: trackingContext,
            applicationSource,
            ctaText: InquiryButtonTypeName.PROMOTIONS
        });
    }, [applicationSource, trackingContext]);

    const handlePromotionInquiryClick = (openModal: IApplicationModalButtonRenderProps["openModal"]) => {
        if (trackingContext) {
            trackAmplitudeOfferSelectPromotion(trackingContext);
        }

        if (amplitudeLeadFormTrackingContext) {
            openModal({gtm: gtmData, amplitude: amplitudeLeadFormTrackingContext});
            return;
        }

        openModal(gtmData);
    };

    return (
        <li css={promotionLi} key={promotion.id} data-id={elementId}>
            <div css={promotionIconColumnStyle}>{promotionStyle.icon}</div>

            <div>
                <Text css={promotionTypeNameStyle(promotionStyle.color)} as="p" variant="mini_header">
                    {getPromotionTypeName(promotion.type)}
                </Text>

                <Text as="p" variant="headline_6" css={promotionNameStyle}>
                    {promotion.name}
                </Text>

                <Text as="p" variant="body_copy_2" css={promotionDescription}>
                    {promotion.description && promotion.description}
                </Text>

                <ApplicationModalButton
                    flowVariant="promotion"
                    initialStep={ApplicationModalStep.PROMOTION_FORM}
                    source={applicationSource}
                    sourceSection={ApplicationSourceSection.MODAL}
                    vendorId={vendorId}
                    offerId={offerId || null}
                    propertyId={propertyId || null}
                    render={({openModal}) => (
                        <Button
                            type="button"
                            onClick={() => handlePromotionInquiryClick(openModal)}
                            variant="outlined_secondary"
                            size="x-small"
                            css={promotionButtonStyle}
                        >
                            {InquiryButtonTypeName.PROMOTIONS}
                        </Button>
                    )}
                />
            </div>
        </li>
    );
};

const promotionLi = (theme: Theme) => css`
    display: flex;

    &:not(:last-of-type) {
        margin: 0 0 5.8rem 0;
    }

    @media (min-width: ${theme.breakpoints.md}) {
        &:not(:last-of-type) {
            margin: 0 0 5.6rem 0;
        }
    }
`;

const promotionIconColumnStyle = (theme: Theme) => css`
    flex: 0 0 75px;

    @media (min-width: ${theme.breakpoints.md}) {
        flex: 0 0 95px;
    }
`;

const promotionNameStyle = css`
    margin: 0 0 1.6rem 0;
`;

export const promotionTypeNameStyle = (color: string) => css`
    background-color: ${color};
    text-transform: uppercase;
    display: inline-flex;
    margin: 0 0 1.6rem 0;
    padding: 0 0.1rem;
`;

const promotionDescription = css`
    margin-bottom: 1.6rem;
`;

const promotionButtonStyle = css`
    padding: 1rem 1.4rem;
`;

//  usePromotionStyle - Style configuration hook
interface IGetPromotionStyleOutput {
    icon: React.JSX.Element | null;
    color: string;
}

export function usePromotionStyle(type: PromotionType): IGetPromotionStyleOutput {
    const theme = useTheme();

    switch (type) {
        case PromotionType.OPEN_DAYS:
            return {
                icon: <MegaphoneIcon size="2.56" wrapperSize="5" wrapperColor={theme.colors.background["200"]} />,
                color: theme.colors.background["200"]
            };
        case PromotionType.DISCOUNT:
            return {
                icon: <PercentIcon size="2.56" wrapperSize="5" wrapperColor={theme.colors.info} />,
                color: theme.colors.info
            };
        case PromotionType.LAST_MINUTE:
            return {icon: <CartTimerIcon size="2.56" wrapperSize="5" wrapperColor={theme.colors.warning} />, color: theme.colors.warning};
        case PromotionType.PRESALE:
            return {icon: <MegaphoneIcon size="2.56" wrapperSize="5" wrapperColor={theme.colors.success} />, color: theme.colors.success};
        case PromotionType.EXHIBITION: //TODO: Not used anymore, remove implementation and usages in the app
            return {icon: null, color: "gray"};
        default:
            //  OFFER
            return {
                icon: <HotIcon size="2.56" wrapperSize="5" wrapperColor={theme.colors.highlight} />,
                color: theme.colors.highlight
            };
    }
}
import {useMemo} from "react";
import {css, Theme, useTheme} from "@emotion/react";

import {Button} from "@pg-design/button-module";
import {CartTimerIcon, HotIcon, MegaphoneIcon, PercentIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";

import {InquiryButtonTypeName} from "../../../constants/inquiry_button_type";
import {ApplicationModalButton, IApplicationModalButtonRenderProps} from "../../application/components/ApplicationModalButton";
import {ApplicationModalStep} from "../../application/types/ApplicationModalStep";
import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {IGTMEventLeadForm} from "../../tracking/google_tag_manager/gtm_event_form_open";
import {getPromotionTypeName, PromotionType} from "../helpers/PromotionType";
import {useAmplitudePromotionViewportTracking} from "../hooks/use_amplitude_promotion_viewport_tracking";
import {getAmplitudeOfferPagePromotionTrackingContext} from "../tracking/get_amplitude_offer_page_promotion_tracking_context";
import {getAmplitudePromotionLeadFormTrackingContext} from "../tracking/get_amplitude_promotion_lead_form_event_tracking";
import {trackAmplitudeOfferSelectPromotion} from "../tracking/track_amplitude_offer_promotion_events";

interface IProps {
    promotion: {
        description: string | null;
        id: number;
        name: string;
        type: PromotionType;
    };
    vendorId: number;
    isAutoresponderEnabled: boolean;
    offerId?: number;
    propertyId?: number;
    isSaleOverLimit?: boolean;
}

export const Promotion = (props: IProps) => {
    const {promotion, vendorId, offerId, propertyId} = props;

    const promotionStyle = usePromotionStyle(promotion.type);
    const applicationSource = propertyId ? ApplicationSource.PromotionProperty : ApplicationSource.Promotion;
    const gtmData: IGTMEventLeadForm = {
        clickText: InquiryButtonTypeName.PROMOTIONS,
        source: GTMEventApplicationSourceSection.PROMOTION,
        vendorName: vendorId.toString(),
        offerName: offerId ? offerId.toString() : ""
    };

    const trackingContext = useMemo(() => {
        if (!offerId) {
            return null;
        }

        return getAmplitudeOfferPagePromotionTrackingContext({
            promotion,
            offerId,
            propertyId,
            creativeSlot: "promotion_box"
        });
    }, [offerId, propertyId, promotion]);

    const {elementId} = useAmplitudePromotionViewportTracking({
        trackingContext,
        elementId: promotion.id.toString(),
        disabled: !offerId
    });

    const amplitudeLeadFormTrackingContext = useMemo(() => {
        if (!trackingContext) {
            return null;
        }

        return getAmplitudePromotionLeadFormTrackingContext({
            promotionTrackingContext: trackingContext,
            applicationSource,
            ctaText: InquiryButtonTypeName.PROMOTIONS
        });
    }, [applicationSource, trackingContext]);

    const handlePromotionInquiryClick = (openModal: IApplicationModalButtonRenderProps["openModal"]) => {
        if (trackingContext) {
            trackAmplitudeOfferSelectPromotion(trackingContext);
        }

        if (amplitudeLeadFormTrackingContext) {
            openModal({gtm: gtmData, amplitude: amplitudeLeadFormTrackingContext});
            return;
        }

        openModal(gtmData);
    };

    return (
        <li css={promotionLi} key={promotion.id} data-id={elementId}>
            <div css={promotionIconColumnStyle}>{promotionStyle.icon}</div>

            <div>
                <Text css={promotionTypeNameStyle(promotionStyle.color)} as="p" variant="mini_header">
                    {getPromotionTypeName(promotion.type)}
                </Text>

                <Text as="p" variant="headline_6" css={promotionNameStyle}>
                    {promotion.name}
                </Text>

                <Text as="p" variant="body_copy_2" css={promotionDescription}>
                    {promotion.description && promotion.description}
                </Text>

                <ApplicationModalButton
                    flowVariant="promotion"
                    initialStep={ApplicationModalStep.PROMOTION_FORM}
                    source={applicationSource}
                    sourceSection={ApplicationSourceSection.MODAL}
                    vendorId={vendorId}
                    offerId={offerId || null}
                    propertyId={propertyId || null}
                    render={({openModal}) => (
                        <Button
                            type="button"
                            onClick={() => handlePromotionInquiryClick(openModal)}
                            variant="outlined_secondary"
                            size="x-small"
                            css={promotionButtonStyle}
                        >
                            {InquiryButtonTypeName.PROMOTIONS}
                        </Button>
                    )}
                />
            </div>
        </li>
    );
};

const promotionLi = (theme: Theme) => css`
    display: flex;

    &:not(:last-of-type) {
        margin: 0 0 5.8rem 0;
    }

    @media (min-width: ${theme.breakpoints.md}) {
        &:not(:last-of-type) {
            margin: 0 0 5.6rem 0;
        }
    }
`;

const promotionIconColumnStyle = (theme: Theme) => css`
    flex: 0 0 75px;

    @media (min-width: ${theme.breakpoints.md}) {
        flex: 0 0 95px;
    }
`;

const promotionNameStyle = css`
    margin: 0 0 1.6rem 0;
`;

export const promotionTypeNameStyle = (color: string) => css`
    background-color: ${color};
    text-transform: uppercase;
    display: inline-flex;
    margin: 0 0 1.6rem 0;
    padding: 0 0.1rem;
`;

const promotionDescription = css`
    margin-bottom: 1.6rem;
`;

const promotionButtonStyle = css`
    padding: 1rem 1.4rem;
`;

//  usePromotionStyle - Style configuration hook
interface IGetPromotionStyleOutput {
    icon: React.JSX.Element | null;
    color: string;
}

export function usePromotionStyle(type: PromotionType): IGetPromotionStyleOutput {
    const theme = useTheme();

    switch (type) {
        case PromotionType.OPEN_DAYS:
            return {
                icon: <MegaphoneIcon size="2.56" wrapperSize="5" wrapperColor={theme.colors.background["200"]} />,
                color: theme.colors.background["200"]
            };
        case PromotionType.DISCOUNT:
            return {
                icon: <PercentIcon size="2.56" wrapperSize="5" wrapperColor={theme.colors.info} />,
                color: theme.colors.info
            };
        case PromotionType.LAST_MINUTE:
            return {icon: <CartTimerIcon size="2.56" wrapperSize="5" wrapperColor={theme.colors.warning} />, color: theme.colors.warning};
        case PromotionType.PRESALE:
            return {icon: <MegaphoneIcon size="2.56" wrapperSize="5" wrapperColor={theme.colors.success} />, color: theme.colors.success};
        case PromotionType.EXHIBITION: //TODO: Not used anymore, remove implementation and usages in the app
            return {icon: null, color: "gray"};
        default:
            //  OFFER
            return {
                icon: <HotIcon size="2.56" wrapperSize="5" wrapperColor={theme.colors.highlight} />,
                color: theme.colors.highlight
            };
    }
}
